import { decode, encode } from '@msgpack/msgpack';

export function useMessagePack() {

    /**
     * Decodes the response data based on the content type or its format. It supports decoding of MessagePack and JSON,
     * and if neither applies, the response data is returned as-is.
     *
     * @param {Object} response - The response object containing headers and data to be decoded
     * @param {Object.<string, string>} response.headers - The headers of the response, which may include content type info
     * @param {ArrayBuffer|any} response.data - The raw response data which may be an ArrayBuffer or another type
     * @return {Object|Array|string|any} - Returns the decoded data as an object, array, string, or the original data
     * @throws {Error} - Throws if JSON parsing fails or if other decoding errors occur
     */
    function decodeResponse(response) {
        const contentType = response.headers?.['content-type'] || '';
        const isMessagePack = contentType.includes('application/msgpack');
        // Safely check for Buffer in both Browser and Node environments
        const isNodeBuffer = typeof Buffer !== 'undefined' && Buffer.isBuffer && Buffer.isBuffer(response.data);
        const isArrayBuffer = response.data instanceof ArrayBuffer;
        const isBuffer = isArrayBuffer || isNodeBuffer;
        // Convert binary data to Uint8Array (done once to avoid repetition)
        const getDataArray = () => new Uint8Array(response.data);
        if (isMessagePack && isBuffer) {
            return decode(getDataArray());
        } else if (isBuffer) {
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(getDataArray());
            try {
                return JSON.parse(text);
            } catch (error) {
                console.warn('Failed to parse JSON from binary data:', error);
                return response.data;
            }
        } else {
            return response.data;
        }
    }

    /**
     * Encodes the provided data into MessagePack format.
     *
     * @param {any} data - The data to be encoded (can be any serializable JavaScript value)
     * @return {Uint8Array} - The encoded result as a Uint8Array containing the MessagePack data
     */
    function encodeData(data) {
        return encode(data);
    }

    return {
        decodeResponse,
        encodeData
    };
}
